import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type ExecucaoRm, type MudancaRm } from '../dados/api.js'
import { useUsuarioAtual } from '../dados/usuarioAtual.js'
import { QuandoFoi, rotuloPerfil } from './admin-comum.js'

/// Perfis e obras que seguem o RM: o que chegou, o que mudaria, e os botões.
///
/// Começa em **prévia**: a lista chega, o sistema calcula o que mudaria, e nada é
/// alterado até o supremo aplicar — é quando o SESMT confere se a tabela de função
/// para perfil faz sentido. Depois, pode ligar a automática.

export function SincronizacaoRm() {
  const cliente = useQueryClient()
  const { usuario: eu } = useUsuarioAtual()
  const souSupremo = eu?.superAdmin ?? false
  const [aberto, setAberto] = useState(false)
  const [confirmando, setConfirmando] = useState<'aplicar' | 'modo' | null>(null)
  const [motivo, setMotivo] = useState('')

  const estado = useQuery({ queryKey: ['admin', 'integracao-rm'], queryFn: () => api.adminIntegracaoRm() })

  const aplicar = useMutation({
    mutationFn: (id: string) => api.adminAplicarIntegracaoRm(id),
    onSuccess: () => {
      setConfirmando(null)
      void cliente.invalidateQueries({ queryKey: ['admin'] })
    },
  })
  const lerAgora = useMutation({
    mutationFn: () => api.adminLerRmAgora(),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['admin'] }),
  })
  const trocarModo = useMutation({
    mutationFn: (modo: 'previa' | 'automatico') => api.adminModoIntegracaoRm(modo, motivo.trim() || undefined),
    onSuccess: () => {
      setConfirmando(null)
      setMotivo('')
      void cliente.invalidateQueries({ queryKey: ['admin', 'integracao-rm'] })
    },
  })

  const d = estado.data
  if (!d) return null
  const u = d.ultima
  const automatica = d.modo === 'automatico'
  const leitura = d.leituraDireta?.configurada ? d.leituraDireta : null

  return (
    <div className="cartao mb-4 p-3">
      <button onClick={() => setAberto(!aberto)} className="flex w-full items-center gap-2 text-left">
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-slate-800">Perfis e obras pelo RM</span>
          <span className="block text-xs text-slate-500">
            {!d.configurada
              ? 'Ainda não configurada no servidor.'
              : !u
                ? 'Esperando a primeira lista do RM.'
                : (
                    <>
                      Última lista <QuandoFoi iso={u.recebidoEm} /> · {u.pessoasNoRm.toLocaleString('pt-BR')} pessoas
                      com e-mail ·{' '}
                      {automatica ? 'aplicação automática' : u.aplicadaEm ? 'aplicada' : 'prévia, nada foi alterado'}
                    </>
                  )}
          </span>
        </span>
        <span
          className={`etiqueta shrink-0 ${automatica ? 'bg-leve/10 text-leve' : 'bg-moderada/10 text-moderada'}`}
        >
          {automatica ? 'Automática' : 'Prévia'}
        </span>
        <span aria-hidden className="text-slate-400">
          {aberto ? '▲' : '▾'}
        </span>
      </button>

      {aberto && (
        <div className="mt-3 space-y-3 border-t border-slate-100 pt-3 text-sm">
          <p className="text-xs text-slate-600">
            O perfil sai da <strong>função</strong> e a obra sai da <strong>seção</strong> do funcionário no RM, com
            as mesmas regras dos fluxos de dados do Power Apps. Quem foi ajustado à mão aqui não é tocado.
            Administrador nunca vem do RM.
          </p>

          {leitura && (
            <div className="rounded-lg border border-slate-200 p-2 text-xs text-slate-600">
              <p>
                O sistema lê o RM direto do banco a cada <strong>{leitura.intervaloMin} minutos</strong>. Uma lista
                só entra aqui quando algo mudou no RM.
              </p>
              {leitura.ultima && (
                <p className={`mt-1 ${leitura.ultima.resultado === 'erro' ? 'text-alta' : ''}`}>
                  Última leitura <QuandoFoi iso={leitura.ultima.em} />:{' '}
                  {leitura.ultima.resultado === 'erro'
                    ? `falhou (${leitura.ultima.erro ?? 'sem detalhe'})`
                    : leitura.ultima.resultado === 'sem_mudanca'
                      ? 'nada mudou no RM'
                      : 'lista nova recebida'}
                  {leitura.ultima.total !== null && leitura.ultima.resultado !== 'erro' &&
                    ` · ${leitura.ultima.total.toLocaleString('pt-BR')} pessoas com e-mail`}
                </p>
              )}
            </div>
          )}

          {u && <Resultado execucao={u} />}

          <Regras regras={d.regras} />

          {souSupremo && leitura && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => lerAgora.mutate()}
                disabled={lerAgora.isPending}
                className="botao-neutro !min-h-0 !py-1.5 text-sm"
              >
                {lerAgora.isPending ? 'Lendo o RM…' : 'Ler o RM agora'}
              </button>
              {lerAgora.data && (
                <span className="text-xs text-leve" role="status">
                  {lerAgora.data.total.toLocaleString('pt-BR')} pessoas lidas
                  {lerAgora.data.aplicada ? ' e aplicadas.' : '.'}
                </span>
              )}
              {lerAgora.error && (
                <span className="text-xs text-alta" role="alert">
                  {(lerAgora.error as Error).message}
                </span>
              )}
            </div>
          )}

          {souSupremo && u && (
            <div className="space-y-2 rounded-lg bg-slate-50 p-2">
              {confirmando === 'aplicar' ? (
                <Confirmacao
                  texto={`Aplicar agora a lista de ${new Date(u.recebidoEm).toLocaleString('pt-BR')}? ${
                    u.totais.pessoasAlteradas + u.totais.novas
                  } cadastros mudam. O cálculo é refeito contra o banco de agora.`}
                  ocupado={aplicar.isPending}
                  onSim={() => aplicar.mutate(u.id)}
                  onNao={() => setConfirmando(null)}
                />
              ) : confirmando === 'modo' ? (
                <>
                  <input
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Motivo (fica no registro)"
                    className="campo"
                  />
                  <Confirmacao
                    texto={
                      automatica
                        ? 'Voltar para prévia? As próximas listas só mostram o que mudaria.'
                        : 'Ligar a aplicação automática? Cada lista do RM passa a valer sozinha, menos quando algo parecer fora do normal.'
                    }
                    ocupado={trocarModo.isPending}
                    onSim={() => trocarModo.mutate(automatica ? 'previa' : 'automatico')}
                    onNao={() => setConfirmando(null)}
                  />
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {!u.aplicadaEm && (
                    <button onClick={() => setConfirmando('aplicar')} className="botao-primario !min-h-0 !py-1.5 text-sm">
                      Aplicar esta lista
                    </button>
                  )}
                  <button onClick={() => setConfirmando('modo')} className="botao-neutro !min-h-0 !py-1.5 text-sm">
                    {automatica ? 'Voltar para prévia' : 'Ligar automática'}
                  </button>
                </div>
              )}
              {(aplicar.error || trocarModo.error) && (
                <p className="rounded bg-alta/10 px-3 py-2 text-sm text-alta" role="alert">
                  {((aplicar.error ?? trocarModo.error) as Error).message}
                </p>
              )}
              {aplicar.data && (
                <p className="text-sm text-leve" role="status">
                  Aplicada. {aplicar.data.totais.pessoasAlteradas + aplicar.data.totais.novas} cadastros atualizados.
                </p>
              )}
            </div>
          )}
          {!souSupremo && (
            <p className="text-xs text-slate-500">Só o administrador supremo aplica e liga a automática.</p>
          )}
        </div>
      )}
    </div>
  )
}

function Resultado({ execucao: u }: { execucao: ExecucaoRm }) {
  const t = u.totais
  const numeros: [string, number][] = [
    ['Cadastros novos', t.novas],
    ['Pessoas alteradas', t.pessoasAlteradas],
    ['Obras incluídas', t.obrasIncluidas],
    ['Obras retiradas', t.obrasRetiradas],
    ['Perfis incluídos', t.perfisIncluidos],
    ['Perfis retirados', t.perfisRetirados],
    ['Saíram do RM', t.sairamDoRm],
    ['Ajustados à mão', t.usuariosComAjusteManual],
  ]
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {u.aplicadaEm ? `Aplicada (${u.aplicadaPor})` : 'O que esta lista mudaria'}
      </p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {numeros.map(([rotulo, n]) => (
          <div key={rotulo} className="rounded-lg bg-slate-50 p-2 text-center">
            <p className="text-lg font-bold text-marinho">{n.toLocaleString('pt-BR')}</p>
            <p className="text-[11px] text-slate-500">{rotulo}</p>
          </div>
        ))}
      </div>
      {u.avisos.length > 0 && (
        <ul className="rounded-lg border border-moderada/30 bg-moderada/5 p-2 text-xs text-moderada">
          {u.avisos.map((a) => (
            <li key={a}>⚠ {a}</li>
          ))}
          <li className="mt-1 text-slate-600">A aplicação automática fica parada até alguém conferir e aplicar à mão.</li>
        </ul>
      )}
      <p className="text-xs text-slate-500">
        {u.ignorados.semEmail.toLocaleString('pt-BR')} funcionários sem e-mail no RM ficaram de fora
        {u.ignorados.obrasDesconhecidas.length > 0 &&
          ` · seções sem obra no QSMS: ${u.ignorados.obrasDesconhecidas.slice(0, 12).join(', ')}${
            u.ignorados.obrasDesconhecidas.length > 12 ? '…' : ''
          }`}
        {(u.ignorados.emailsCompartilhados?.length ?? 0) > 0 &&
          ` · e-mail em pessoas diferentes no RM, deixado de fora até o DP corrigir: ${u.ignorados.emailsCompartilhados.join(', ')}`}
      </p>

      {u.mudancas && u.mudancas.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs font-semibold text-marinho">
            Ver alterações ({Math.min(u.mudancas.length, t.pessoasAlteradas)} de {t.pessoasAlteradas})
          </summary>
          <ul className="mt-1 max-h-72 space-y-1 overflow-y-auto text-xs">
            {u.mudancas.map((m) => (
              <LinhaMudanca key={m.usuarioId} m={m} />
            ))}
          </ul>
        </details>
      )}
      {u.novas && u.novas.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs font-semibold text-marinho">
            Ver cadastros novos ({Math.min(u.novas.length, t.novas)} de {t.novas})
          </summary>
          <ul className="mt-1 max-h-60 space-y-1 overflow-y-auto text-xs text-slate-700">
            {u.novas.map((n) => (
              <li key={n.email}>
                <strong>{n.nome}</strong> · {n.email} · obras {n.obrasCodigos.join(', ')} ·{' '}
                {n.perfis.map((p) => rotuloPerfil[p]).join(', ')}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

function LinhaMudanca({ m }: { m: MudancaRm }) {
  const partes = [
    m.incluirObrasCodigos.length > 0 && `+ obras ${m.incluirObrasCodigos.join(', ')}`,
    m.retirarObrasCodigos.length > 0 && `− obras ${m.retirarObrasCodigos.join(', ')}`,
    m.incluirPerfis.length > 0 && `+ ${m.incluirPerfis.map((p) => rotuloPerfil[p]).join(', ')}`,
    m.retirarPerfis.length > 0 && `− ${m.retirarPerfis.map((p) => rotuloPerfil[p]).join(', ')}`,
  ].filter(Boolean)
  return (
    <li className="text-slate-700">
      <strong>{m.nome}</strong>
      {m.saiuDoRm && <span className="etiqueta ml-1 bg-slate-100 text-slate-500">saiu do RM</span>}
      <span className="text-slate-500"> · {partes.join(' · ')}</span>
    </li>
  )
}

function Regras({ regras }: { regras: { perfil: keyof typeof rotuloPerfil; quando: string[][] }[] }) {
  return (
    <details>
      <summary className="cursor-pointer text-xs font-semibold text-marinho">
        Tabela de função para perfil (para o SESMT conferir)
      </summary>
      <table className="mt-1 w-full text-left text-xs">
        <tbody>
          {regras.map((r) => (
            <tr key={r.perfil} className="border-t border-slate-100 align-top">
              <td className="py-1 pr-2 font-semibold text-slate-700">{rotuloPerfil[r.perfil]}</td>
              <td className="py-1 text-slate-600">
                função contém {r.quando.map((g) => g.join(' + ')).join(' ou ')}
              </td>
            </tr>
          ))}
          <tr className="border-t border-slate-100">
            <td className="py-1 pr-2 font-semibold text-slate-700">Colaborador</td>
            <td className="py-1 text-slate-600">todo funcionário ativo com seção de obra</td>
          </tr>
        </tbody>
      </table>
    </details>
  )
}

function Confirmacao({
  texto,
  ocupado,
  onSim,
  onNao,
}: {
  texto: string
  ocupado: boolean
  onSim: () => void
  onNao: () => void
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-700">{texto}</p>
      <div className="flex gap-2">
        <button onClick={onSim} disabled={ocupado} className="botao-primario flex-1 !min-h-0 !py-1.5 text-sm">
          {ocupado ? 'Aplicando…' : 'Confirmar'}
        </button>
        <button onClick={onNao} disabled={ocupado} className="botao-neutro flex-1 !min-h-0 !py-1.5 text-sm">
          Cancelar
        </button>
      </div>
    </div>
  )
}
